import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { handleError } from '@common/error';
import { useRESTQuery } from '@hooks/useRESTQuery';
import Loader from '@components/Loader';

// // Set up the worker URL for PDF.js
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// pdfjs.GlobalWorkerOptions.workerSrc = '/node_modules/pdfjs-dist/build/pdf.worker.min.js';
// pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;
// or, if using from public folder:
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';

interface Highlight {
  highlighted: string;
  page_number: number;
}

interface PdfViewerProps {
  fileUrl: string;
  source: Highlight[];
  disableHighLight?: boolean;
}

function PdfViewer({
  fileUrl,
  source,
  disableHighLight = false,
}: PdfViewerProps): React.JSX.Element {
  const dispatch = useDispatch();
  const [scale, setScale] = useState<number>(1.5);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numPages, setNumPages] = useState<number | null>(null);

  const { data, isLoading } = useRESTQuery(
    ['download-data', fileUrl],
    {
      endpoint: `viewPoints/download?url=${fileUrl}`,
      method: 'GET',
      headers: {
        'X-Tifin-Ai-Auth': localStorage.getItem('x-tifin-ai-token'),
      },
    },
    {
      enabled: !!fileUrl,
      select: res => res.data,
      onError: (error: any) => {
        handleError(dispatch, error);
      },
    },
  );

  const chunks: string[] = useMemo(() => {
    return source.flatMap((item: Highlight) => {
      const words = item.highlighted.split(/\s+/);
      const chunkArr: string[] = [];
      for (let i = 0; i < words.length; i += 3) {
        chunkArr.push(words.slice(i, i + 3).join(' '));
      }
      return chunkArr;
    });
  }, [source]);

  // Custom text renderer function to highlight matches (simplified approach)
  const textRenderer = (textItem: any) => {
    const shouldHighlight =
      !disableHighLight &&
      chunks.some(highlight => textItem.str.includes(highlight));

    return shouldHighlight ? (
      <mark style={{ backgroundColor: 'rgba(255, 255, 0, 0.4)' }}>
        {textItem.str}
      </mark>
    ) : (
      textItem.str
    );
  };

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  function changePage(offset: number) {
    setPageNumber(prevPageNumber => {
      const newPage = prevPageNumber + offset;
      return newPage >= 1 && newPage <= (numPages || 1)
        ? newPage
        : prevPageNumber;
    });
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  function zoomIn() {
    setScale(prevScale => Math.min(prevScale + 0.1, 3));
  }

  function zoomOut() {
    setScale(prevScale => Math.max(prevScale - 0.1, 0.5));
  }

  return (
    <>
      {isLoading ? (
        <Loader text="Getting everything ready with your document…" />
      ) : (
        <div
          style={{
            height: '80vh',
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px',
              backgroundColor: '#f5f5f5',
              marginBottom: '10px',
              alignItems: 'center',
            }}
          >
            <div>
              <button
                onClick={previousPage}
                disabled={pageNumber <= 1}
                style={{
                  padding: '6px 16px',
                  backgroundColor: pageNumber <= 1 ? '#e0e0e0' : '#1976d2',
                  color: pageNumber <= 1 ? '#9e9e9e' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: pageNumber <= 1 ? 'default' : 'pointer',
                  marginRight: '10px',
                  fontWeight: 500,
                }}
              >
                Previous
              </button>
              <span style={{ margin: '0 10px' }}>
                Page {pageNumber} of {numPages || '--'}
              </span>
              <button
                onClick={nextPage}
                disabled={pageNumber >= (numPages || 0)}
                style={{
                  padding: '6px 16px',
                  backgroundColor:
                    pageNumber >= (numPages || 0) ? '#e0e0e0' : '#1976d2',
                  color: pageNumber >= (numPages || 0) ? '#9e9e9e' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: pageNumber >= (numPages || 0) ? 'default' : 'pointer',
                  marginLeft: '10px',
                  fontWeight: 500,
                }}
              >
                Next
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                onClick={zoomOut}
                style={{
                  padding: '4px 10px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                -
              </button>
              <span style={{ margin: '0 10px' }}>
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={zoomIn}
                style={{
                  padding: '4px 10px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                +
              </button>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Document
              file={`data:application/pdf;base64,${data}`}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <Loader text="Getting everything ready with your document…" />
              }
              error={<div>Failed to load PDF document.</div>}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                customTextRenderer={textRenderer}
              />
            </Document>
          </div>
        </div>
      )}
    </>
  );
}

export default PdfViewer;
